import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { ScipUiSettings, ScipUiSettingsService } from '../../services';
import { take, takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';

// TODO: Currently only show/hide is supported, "collapsed" (panel) should not "viewContainer.clear()"
type DashboardChartSelectors = 'productsBySubmissionStatusShow' | 'submissionsByStatusShow' | 'submissionsByYearShow' | 'submissionsByOrganisationShow';
type DashboardTableSelectors = 'failedSubmissionsShow' | 'productsWithoutSubmissionsShow';
type DashboardSelectors = DashboardChartSelectors | DashboardTableSelectors | 'recentSearchesShow';

export type ValidSelectors = DashboardSelectors;

@Directive({
  selector: '[scipUiSettings]'
})
export class UiSettingsDirective implements OnDestroy, OnInit {
  /***
   * Use the "listen" boolean if the attribute you want to react to can be changed while the component is using it
   * */
  @Input() scipUiSettingsListen: boolean;

  @Input()
  set scipUiSettings(settingsSelector: ValidSelectors) {
    this._settingsSelector = settingsSelector;
  }

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  private _settingsSelector: ValidSelectors;
  private currentlyVisible; // Used as indicator if it needs to be added or removed or nothing needs to be done

  constructor(private viewContainer: ViewContainerRef, private templateRef: TemplateRef<unknown>, private uiSettings: ScipUiSettingsService) {}

  static showTile(settingsSelector: ValidSelectors, uiSettings: ScipUiSettings): boolean {
    switch (settingsSelector) {
      case 'failedSubmissionsShow':
        return uiSettings.dashboard.tableTiles.failedSubmissions.show;
      case 'productsWithoutSubmissionsShow':
        return uiSettings.dashboard.tableTiles.productsWithoutSubmissions.show;
      case 'recentSearchesShow':
        return uiSettings.dashboard.other.recentSearches.show;
      case 'submissionsByStatusShow':
        return uiSettings.dashboard.chartTiles.submissionsByStatus.show;
      case 'productsBySubmissionStatusShow':
        return uiSettings.dashboard.chartTiles.productsBySubmissionsStatus.show;
      case 'submissionsByYearShow':
        return uiSettings.dashboard.chartTiles.submissionByYear.show;
      case 'submissionsByOrganisationShow':
        return uiSettings.dashboard.chartTiles.submissionByOrganisation.show;
      default:
        return true;
    }
  }

  ngOnInit(): void {
    this.setupVisibilityAndListener();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private setupVisibilityAndListener(): void {
    this.uiSettings
      .getScipUiSettings()
      .pipe(this.scipUiSettingsListen ? takeUntil(this.destroyed$) : take(1))
      .subscribe((updatedSettings) => {
        this.showOrHide(updatedSettings);
      });
  }

  private showOrHide(allUiSettings: ScipUiSettings) {
    const showTile = UiSettingsDirective.showTile(this._settingsSelector, allUiSettings);
    if (showTile && !this.currentlyVisible) {
      this.currentlyVisible = true;
      this.viewContainer.createEmbeddedView(this.templateRef);
    } else if (!showTile && this.currentlyVisible) {
      this.currentlyVisible = false;
      this.viewContainer.clear();
    }
  }
}
